import { FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { Carousel } from 'dibs-carousel';
import { Swiper } from 'dibs-pinch-zoom-swipe';

import { HpRespModuleContainer } from '../HpRespModuleContainer/HpRespModuleContainer';
import { SharedCarouselDot } from '../../sharedComponents/SharedCarouselDot/SharedCarouselDot';
import LargeItemCarouselItem from './LargeItemCarouselItem';

import { RenderIn, device } from '../../utils/RenderIn';
import { HpRespCarouselTracking } from '../HpRespCarouselTracking/HpRespCarouselTracking';
import { trackProductImpression, trackProductClick } from './helpers/HeroTracking';
import { SharedInViewport } from '../../sharedComponents/SharedInViewport';
import { TABLET_ITEMS_PER_PAGE } from '../constants';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import {
    trackPromoClick,
    trackPromoImpressions,
    buildPromoTrackingObject,
} from '../helpers/promoTrackingHelpers';
import { trackModuleLocation } from '../helpers/moduleLocationTracking';

import styles from './HpRespHeroModule.scss';

/**
 *  Removes items residue because we want to make sure that all of the carousel pages are full
 */
function removeItemsResidue({
    totalItems,
    itemsPerPage,
}: {
    totalItems: number;
    itemsPerPage: number;
}): number {
    const residue = totalItems % itemsPerPage;

    if (residue !== 0) {
        return totalItems - residue;
    }

    return totalItems;
}

import { HpRespHeroModule_componentModule } from './__generated__/HpRespHeroModule_componentModule.graphql';

type CarouselItems = NonNullable<HpRespHeroModule_componentModule['items']>;

type Props = {
    componentModule: HpRespHeroModule_componentModule;
    useLazyLoadImages: boolean;
    moduleIndex: number;
    totalModules: number;
};

const HpRespHeroModule: FC<Props> = ({
    componentModule,
    useLazyLoadImages,
    moduleIndex,
    totalModules,
}) => {
    if (!componentModule) {
        return null;
    }

    const { title } = componentModule;
    const items = (componentModule.items || []).filter(filterNulls);

    const onItemsImpression = ({
        visibleItems,
        index,
    }: {
        visibleItems: CarouselItems;
        index: number;
    }): void => {
        trackProductImpression({
            items: visibleItems,
            startIndex: index,
        });
        const promoTrackingObjects = visibleItems.map(item => {
            const trackingItem = {
                ...item,
                linkData: {
                    path: item?.link,
                },
            };
            return buildPromoTrackingObject({
                id: 'large-item-carousel',
                name: 'Large Item Carousel',
                item: trackingItem,
                index,
            });
        });
        trackPromoImpressions(promoTrackingObjects);
    };

    const handleClick = (index: number): void => {
        const item = items[index];
        trackProductClick({
            item,
            index,
        });
        const promoTrackingObject = buildPromoTrackingObject({
            id: 'large-item-carousel',
            name: 'Large Item Carousel',
            item: {
                ...item,
                linkData: {
                    path: item?.link,
                },
            },
            index,
        });
        trackPromoClick(promoTrackingObject);

        trackModuleLocation({
            label: 'large item carousel module',
            moduleIndex,
            totalModules,
        });
    };

    return (
        <HpRespModuleContainer addTopGap={!title}>
            <HpRespModuleContainer.Header title={title} />
            <RenderIn deviceTypes={[device.DESKTOP, device.TABLET]}>
                {({ currentDevice }: { currentDevice: string }) => {
                    const itemsPerPage =
                        currentDevice === device.DESKTOP ? TABLET_ITEMS_PER_PAGE : 3;
                    const totalItems = removeItemsResidue({
                        totalItems: items.length,
                        itemsPerPage,
                    });
                    return (
                        <HpRespCarouselTracking
                            items={items}
                            itemsPerPage={itemsPerPage}
                            onItemsImpression={onItemsImpression}
                        >
                            {({ handleIndexChange }) => (
                                <SharedInViewport>
                                    {({ inViewport }: { inViewport: boolean }) => (
                                        <Carousel
                                            classNames={{ listWrapper: styles.listWrapper }}
                                            isAutoRun={inViewport && totalItems > itemsPerPage}
                                            dataTn="hero-items-carousel"
                                            showDots={totalItems > itemsPerPage}
                                            hideArrows
                                            totalItems={totalItems}
                                            itemsPerPage={itemsPerPage}
                                            step={itemsPerPage}
                                            renderItem={({
                                                index,
                                                isVisible,
                                            }: {
                                                index: number;
                                                isVisible: boolean;
                                            }) => (
                                                <LargeItemCarouselItem
                                                    item={items[index]}
                                                    isVisible={isVisible}
                                                    useLazyLoadImages={useLazyLoadImages}
                                                    onClick={() => handleClick(index)}
                                                />
                                            )}
                                            onIndexChange={handleIndexChange}
                                            renderDot={({
                                                isCurrentDot,
                                            }: {
                                                isCurrentDot?: boolean;
                                            } = {}) => (
                                                <SharedCarouselDot isActive={isCurrentDot} />
                                            )}
                                        />
                                    )}
                                </SharedInViewport>
                            )}
                        </HpRespCarouselTracking>
                    );
                }}
            </RenderIn>

            <RenderIn deviceTypes={[device.MOBILE]}>
                {() => {
                    const itemsPerPage = 1.5;

                    return (
                        <HpRespCarouselTracking
                            items={items}
                            itemsPerPage={itemsPerPage}
                            onItemsImpression={onItemsImpression}
                        >
                            {({ handlePageChange }) => (
                                <Swiper itemsPerPage={itemsPerPage} onPageChange={handlePageChange}>
                                    {items.map((item, index) => (
                                        <LargeItemCarouselItem
                                            key={item.cmsId || index}
                                            item={item}
                                            useLazyLoadImages={useLazyLoadImages}
                                            onClick={() => handleClick(index)}
                                            isVisible
                                        />
                                    ))}
                                </Swiper>
                            )}
                        </HpRespCarouselTracking>
                    );
                }}
            </RenderIn>
        </HpRespModuleContainer>
    );
};

export default createFragmentContainer(HpRespHeroModule, {
    componentModule: graphql`
        fragment HpRespHeroModule_componentModule on HeroModule {
            title
            items {
                itemId
                cmsId
                link
                imageUrl
                title
                contemporaryTrackingString
            }
        }
    `,
});
